import {
  AssessmentType,
  RlRoundStatus,
  useCompleteAssessmentMutation,
  useGetAssessmentQuery,
  useGetRlEnvironmentQuery,
  useRunRlEnvironmentRoundMutation,
  useStartAssessmentMutation,
  useUpsertRlEnvironmentMutation,
} from '@gql/gql-types';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SendIcon from '@mui/icons-material/Send';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import useTimeLeft from '@pages/interview/hooks/use-time-left';
import formatDuration from '@utils/format-duration';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useParams } from 'react-router';

import {
  AssessmentErrorLoading,
  AssessmentLoading,
  AssessmentNotFound,
} from '../github-coding/components/github-coding-state-views';
import { CodeEditor } from '../live-coding/code-editor';
import { TaskDescription } from '../live-coding/task-description';
import { FileExplorer, GRADER_PATH } from './components/file-explorer';
import { ResultsPanel } from './components/results-panel';

type FileDraft = { path: string; content: string };

const README_PATH = 'README.md';
const MONO = 'Monaco, Menlo, "Ubuntu Mono", monospace';
const ROUND_POLL_MS = 5000;

const languageFor = (path: string) => {
  if (path.endsWith('.json')) {
    return 'json';
  }
  if (path.endsWith('.md')) {
    return 'markdown';
  }
  if (path.endsWith('.py')) {
    return 'python';
  }
  return 'plaintext';
};

const byteLength = (content: string) =>
  new TextEncoder().encode(content).length;

const messageOf = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const ResizeHandle = ({ direction }: { direction: 'x' | 'y' }) => (
  <PanelResizeHandle>
    <Box
      sx={{
        width: direction === 'x' ? '4px' : '100%',
        height: direction === 'x' ? '100%' : '4px',
        backgroundColor: 'divider',
        cursor: direction === 'x' ? 'col-resize' : 'row-resize',
        transition: 'background-color 0.2s ease',
        '&:hover': { backgroundColor: 'primary.main' },
      }}
    />
  </PanelResizeHandle>
);

const RlEnvironmentPage = () => {
  const { id: assessmentIdFromParams } = useParams();
  const assessmentId = assessmentIdFromParams || 'missing-assessment-id';
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

  const {
    loading: isAssessmentLoading,
    data: assessmentData,
    error: assessmentError,
    refetch: refetchAssessment,
  } = useGetAssessmentQuery({
    variables: { id: assessmentId },
  });

  const assessment = assessmentData?.assessment;
  const isRlEnvironment =
    assessment?.template.templateType === AssessmentType.RlEnvironment;

  const {
    loading: isEnvLoading,
    data: envData,
    error: envError,
    refetch: refetchEnv,
    startPolling,
    stopPolling,
  } = useGetRlEnvironmentQuery({
    skip: !isRlEnvironment,
  });

  const [startAssessment, { loading: isStarting }] =
    useStartAssessmentMutation();
  const [completeAssessment, { loading: isCompleting }] =
    useCompleteAssessmentMutation();
  const [upsertRlEnvironment, { loading: isSaving }] =
    useUpsertRlEnvironmentMutation();
  const [runRound, { loading: isRunning }] = useRunRlEnvironmentRoundMutation();

  const env = envData?.getRlEnvironment;
  const task = env?.task;
  const limits = env?.limits;

  const [files, setFiles] = useState<FileDraft[]>([]);
  const [graderText, setGraderText] = useState('{}');
  const [activePath, setActivePath] = useState(README_PATH);
  const [previewMarkdown, setPreviewMarkdown] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isExplorerOpen, setIsExplorerOpen] = useState(false);
  const [isSeeded, setIsSeeded] = useState(false);

  // The editor owns the workspace once the server-seeded files land, so later
  // refetches (polling a running round) must not overwrite unsaved edits.
  useEffect(() => {
    if (isSeeded || !env?.files.length) {
      return;
    }
    const next = env.files.map((file) => ({
      path: file.path,
      content: file.content,
    }));
    setFiles(next);
    setGraderText(JSON.stringify(env.grader ?? {}, null, 2));
    setActivePath(
      next.find((file) => file.path === README_PATH)?.path ?? next[0].path,
    );
    setIsSeeded(true);
  }, [env, isSeeded]);

  const startedAt = useMemo(
    () => (assessment?.startedAt ? new Date(assessment.startedAt) : null),
    [assessment?.startedAt],
  );
  const duration = assessment?.template.duration ?? null;
  const timeLeft = useTimeLeft({ startedAt, duration });

  const latestRound = env?.rounds?.length
    ? env.rounds[env.rounds.length - 1]
    : null;
  const graderResult = latestRound?.graderResult ?? null;
  const isRoundRunning = latestRound?.status === RlRoundStatus.Running;

  // A solver round outlives its mutation when the request times out, so poll
  // until the round lands rather than leaving the pane blank.
  useEffect(() => {
    if (!isRunning && !isRoundRunning) {
      stopPolling();
      return;
    }
    startPolling(ROUND_POLL_MS);
  }, [isRunning, isRoundRunning, startPolling, stopPolling]);

  const editorValue =
    activePath === GRADER_PATH
      ? graderText
      : (files.find((file) => file.path === activePath)?.content ?? '');
  const showPreview = activePath.endsWith('.md') && previewMarkdown;

  const onEditorChange = (code: string) => {
    if (activePath === GRADER_PATH) {
      setGraderText(code);
      return;
    }
    setFiles((previous) =>
      previous.map((file) =>
        file.path === activePath ? { ...file, content: code } : file,
      ),
    );
  };

  const save = useCallback(async () => {
    let grader: Record<string, unknown>;
    try {
      grader = JSON.parse(graderText) as Record<string, unknown>;
    } catch {
      setErrorMessage('grader.json must be valid JSON');
      return false;
    }
    if (limits) {
      if (files.length > limits.maxFiles) {
        setErrorMessage(`Environments are limited to ${limits.maxFiles} files`);
        return false;
      }
      const oversized = [
        ...files,
        { path: GRADER_PATH, content: graderText },
      ].find((file) => byteLength(file.content) > limits.maxFileBytes);
      if (oversized) {
        setErrorMessage(
          `${oversized.path} is over the ${limits.maxFileBytes} byte limit`,
        );
        return false;
      }
    }
    try {
      await upsertRlEnvironment({
        variables: {
          input: {
            files,
            grader,
          },
        },
      });
    } catch (error) {
      setErrorMessage(messageOf(error, 'Could not save your environment'));
      return false;
    }
    setErrorMessage(null);
    await refetchEnv();
    return true;
  }, [files, graderText, limits, refetchEnv, upsertRlEnvironment]);

  const onStart = useCallback(async () => {
    await startAssessment({ variables: { input: { id: assessmentId } } });
    await refetchAssessment();
    await refetchEnv();
  }, [assessmentId, refetchAssessment, refetchEnv, startAssessment]);

  const onRun = useCallback(async () => {
    const saved = await save();
    if (!saved) {
      return;
    }
    try {
      await runRound();
    } catch (error) {
      setErrorMessage(messageOf(error, 'The agent run did not finish'));
    }
    await refetchEnv();
  }, [refetchEnv, runRound, save]);

  // Running out of time swaps the editor for the results screen, so the last
  // edits have to be persisted before that render or they are simply lost.
  const hasFlushedOnTimeout = useRef(false);
  useEffect(() => {
    if (
      timeLeft !== 0 ||
      assessment?.completedAt ||
      !files.length ||
      hasFlushedOnTimeout.current
    ) {
      return;
    }
    hasFlushedOnTimeout.current = true;
    void save();
  }, [assessment?.completedAt, files.length, save, timeLeft]);

  const onSubmit = useCallback(async () => {
    const saved = await save();
    if (!saved) {
      return;
    }
    try {
      await completeAssessment({ variables: { input: { id: assessmentId } } });
    } catch (error) {
      setErrorMessage(messageOf(error, 'Could not submit your environment'));
      return;
    }
    await refetchAssessment();
  }, [assessmentId, completeAssessment, refetchAssessment, save]);

  if (isAssessmentLoading || (isRlEnvironment && isEnvLoading)) {
    return <AssessmentLoading />;
  }
  if (assessmentError || (isRlEnvironment && envError)) {
    return <AssessmentErrorLoading />;
  }
  if (!assessment || !isRlEnvironment) {
    return <AssessmentNotFound />;
  }
  if (assessment.completedAt || timeLeft === 0) {
    return <ResultsPanel assessmentId={assessment.id} />;
  }

  if (!assessment.startedAt) {
    const brief = task?.brief ?? assessment.template.description;
    return (
      <Container sx={{ pt: 4, pb: 6, maxWidth: 'md' }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant='h5' gutterBottom>
              {task?.name ?? assessment.template.name}
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              {brief}
            </Typography>
            {task ? (
              <Chip
                size='small'
                variant='outlined'
                label={task.domain}
                sx={{ mt: 1.5 }}
              />
            ) : null}
          </Box>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'auto',
              maxHeight: '55vh',
            }}
          >
            <TaskDescription
              description={task?.readme ?? brief}
              bottomPadding={3}
            />
          </Box>
          <Stack direction='row' spacing={2} alignItems='center'>
            <Button variant='contained' onClick={onStart} disabled={isStarting}>
              Start assessment
            </Button>
            <Typography variant='body2' color='text.secondary'>
              {formatDuration(duration ?? 0)}, one sitting, no retakes
            </Typography>
          </Stack>
        </Stack>
      </Container>
    );
  }

  const roundsUsed = env?.rounds?.length ?? 0;
  const maxRounds = limits?.maxRounds ?? 0;
  const isOutOfRounds = roundsUsed >= maxRounds;

  const status = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
      <Chip
        size='small'
        variant='outlined'
        label={`Round ${roundsUsed} / ${maxRounds}`}
      />
      <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      <Typography variant='body2' sx={{ fontFamily: MONO }}>
        {formatDuration(timeLeft ?? 0)}
      </Typography>
    </Box>
  );

  const explorer = (
    <FileExplorer
      rootLabel={task?.slug ?? 'environment'}
      paths={[...files.map((file) => file.path), GRADER_PATH]}
      activePath={activePath}
      onSelect={(path) => {
        setActivePath(path);
        setIsExplorerOpen(false);
      }}
    />
  );

  const editorPane = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: { xs: 1.5, md: 2 },
          py: 0.75,
          flexShrink: 0,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant='body2'
          noWrap
          sx={{ fontFamily: MONO, color: 'text.secondary' }}
        >
          {activePath}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {activePath.endsWith('.md') ? (
          <Button
            size='small'
            startIcon={
              showPreview ? <EditOutlinedIcon /> : <VisibilityOutlinedIcon />
            }
            onClick={() => setPreviewMarkdown(!previewMarkdown)}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {showPreview ? 'Edit' : 'Preview'}
          </Button>
        ) : null}
      </Box>

      {errorMessage ? (
        <Alert severity='error' sx={{ borderRadius: 0 }}>
          {errorMessage}
        </Alert>
      ) : null}

      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <PanelGroup direction='vertical'>
          <Panel defaultSize={70} minSize={25}>
            {showPreview ? (
              <TaskDescription description={editorValue} bottomPadding={3} />
            ) : (
              <CodeEditor
                language={languageFor(activePath)}
                value={editorValue}
                onChange={onEditorChange}
              />
            )}
          </Panel>

          <ResizeHandle direction='y' />

          <Panel defaultSize={30} minSize={15}>
            <Box
              sx={{
                height: '100%',
                overflow: 'auto',
                backgroundColor: 'background.paper',
                p: { xs: 1.5, md: 2 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant='overline' sx={{ letterSpacing: 1 }}>
                  Agent
                </Typography>
                {latestRound ? (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: { xs: 'none', md: 'inline' } }}
                  >
                    round {latestRound.roundNumber} · {latestRound.model}
                  </Typography>
                ) : null}
                <Box
                  sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }}
                />
                {isRoundRunning ? (
                  <Chip size='small' variant='outlined' label='running' />
                ) : null}
                {graderResult ? (
                  <Chip
                    size='small'
                    color={graderResult.passed ? 'success' : 'error'}
                    label={graderResult.passed ? 'passed' : 'failed'}
                  />
                ) : null}
                {graderResult?.caseScores.map((score) => (
                  <Chip
                    key={score.id}
                    size='small'
                    variant='outlined'
                    color={score.passed ? 'success' : 'error'}
                    label={score.id}
                  />
                ))}
              </Box>
              <Typography
                component='pre'
                variant='body2'
                sx={{
                  fontFamily: MONO,
                  whiteSpace: 'pre-wrap',
                  m: 0,
                  color: latestRound?.error ? 'error.main' : 'text.primary',
                }}
              >
                {latestRound?.solverOutput ??
                  latestRound?.error ??
                  (isRoundRunning
                    ? 'The agent is working through your environment...'
                    : 'Run the agent to see how it does against your grader.')}
              </Typography>
            </Box>
          </Panel>
        </PanelGroup>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1, md: 1.5 },
          px: { xs: 1.5, md: 2 },
          py: { xs: 1, md: 1.5 },
          flexShrink: 0,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
        >
          {isNarrow ? (
            <IconButton
              size='small'
              aria-label='Show files'
              onClick={() => setIsExplorerOpen(true)}
            >
              <FolderOpenIcon fontSize='small' />
            </IconButton>
          ) : null}
          <Typography
            variant='subtitle1'
            noWrap
            sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }}
          >
            {task?.name ?? assessment.template.name}
          </Typography>
          {isNarrow ? status : null}
        </Box>

        {isNarrow ? null : (
          <>
            <Box sx={{ flexGrow: 1 }} />
            {status}
          </>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            '& > *': { flexGrow: { xs: 1, md: 0 }, whiteSpace: 'nowrap' },
          }}
        >
          <Button
            size='small'
            variant='outlined'
            startIcon={<SaveOutlinedIcon />}
            onClick={save}
            disabled={isSaving}
          >
            Save
          </Button>
          <Button
            size='small'
            variant='outlined'
            startIcon={
              isRunning || isRoundRunning ? (
                <CircularProgress size={14} color='inherit' />
              ) : (
                <PlayArrowIcon />
              )
            }
            onClick={onRun}
            disabled={isRunning || isRoundRunning || isSaving || isOutOfRounds}
          >
            Run agent
          </Button>
          <Button
            size='small'
            variant='contained'
            color='secondary'
            startIcon={<SendIcon />}
            onClick={onSubmit}
            disabled={isCompleting || isSaving}
          >
            Submit
          </Button>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {isNarrow ? (
          <>
            <Drawer
              open={isExplorerOpen}
              onClose={() => setIsExplorerOpen(false)}
            >
              <Box sx={{ width: 260, height: '100%' }}>{explorer}</Box>
            </Drawer>
            {editorPane}
          </>
        ) : (
          <PanelGroup direction='horizontal'>
            <Panel defaultSize={20} minSize={12}>
              {explorer}
            </Panel>
            <ResizeHandle direction='x' />
            <Panel defaultSize={80} minSize={40}>
              {editorPane}
            </Panel>
          </PanelGroup>
        )}
      </Box>
    </Box>
  );
};

export default RlEnvironmentPage;
